import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface Banner {
  hasBeenSeen?: boolean;
  bannerUUID?: string;
}

export interface BannerStorage {
  banner: Banner;
}

export type BannerContextType = {
  getBanner: () => BannerStorage;
  updateBanner: (newBanner: Banner) => Banner;
};

export interface BannerInfoForWidget {
  announcement: ReactNode;
  color: string;
  UUID: string;
}

export interface BannerWidgetData {
  BannerData: BannerInfoForWidget;
}

const bannerContextStorageKey = 'devportal-bannerContext';

const getInitialState = () => {
  const bannerInfo = localStorage.getItem(bannerContextStorageKey);
  return bannerInfo ? JSON.parse(bannerInfo) : null;
};

const bannerContext = createContext<BannerContextType | null>(null);

const BannerContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [bannerData, setBannerData] = useState<BannerStorage>(getInitialState);

  useEffect(() => {
    localStorage.setItem(bannerContextStorageKey, JSON.stringify(bannerData));
  }, [bannerData]);

  useEffect(() => {
    if (!getInitialState()) {
      setBannerData({
        banner: {
          hasBeenSeen: false,
          bannerUUID: '',
        },
      });
    }
  }, []);

  const saveBanner = (newBanner: Banner) => {
    const updatedBanner: Banner = {
      hasBeenSeen: newBanner.hasBeenSeen,
      bannerUUID: newBanner.bannerUUID,
    };

    setBannerData({
      banner: updatedBanner,
    });

    return updatedBanner;
  };

  const getBanner = () => {
    return bannerData;
  };

  const updateBanner = (newBanner: Banner) => {
    saveBanner(newBanner);
    return bannerData.banner;
  };

  return (
    <bannerContext.Provider value={{ getBanner, updateBanner }}>
      {children}
    </bannerContext.Provider>
  );
};

export const useBanner = () => useContext(bannerContext) as BannerContextType;

export default BannerContextProvider;
